import React, {useState, useEffect, useRef} from 'react';
import {useLocation} from 'react-router-dom';
import {hooks} from '../hooks';
import {text} from '../text';
import {theme} from '../constants';
import {components} from '../components';
import axiosInstance from '../services/axiosInstance';
import {formatDateTime} from '../utils/formatText';
import { useLanguage } from '../context/LanguageContext';
import { listCache } from '../utils/listCache';
import { MdAdd, MdBusiness, MdLocationOn, MdAccessTime, MdPerson } from 'react-icons/md';
import InfiniteScroll from 'react-infinite-scroll-component';

const AddIcon = MdAdd as any;
const BusinessIcon = MdBusiness as any;
const LocationIcon = MdLocationOn as any;
const TimeIcon = MdAccessTime as any;
const PersonIcon = MdPerson as any;

export const MarketingActivity: React.FC = () => {
  const {pathname} = useLocation();
  const navigate = hooks.useAppNavigate();
  const { t } = useLanguage();

  const [activities, setActivities] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const restoredRef = useRef(false);

  useEffect(() => {
    setTimeout(() => {
      window.scroll({top: -1, left: 0, behavior: 'smooth'});
    }, 10);
  }, [pathname]);

  const fetchActivities = async (currentOffset: number = 0) => {
    try {
      if (currentOffset === 0) setLoading(true);
      else setIsLoadingMore(true);

      const response = await axiosInstance.get(`/marketing-activities?limit=10&offset=${currentOffset}`);
      const newData = response.data;

      if (newData.length < 10) setHasMore(false);
      else setHasMore(true);

      if (currentOffset === 0) {
        setActivities(newData);
      } else {
        setActivities((prev) => {
          const existingIds = new Set(prev.map(a => a.id));
          const uniqueNew = newData.filter((a: any) => !existingIds.has(a.id));
          return [...prev, ...uniqueNew];
        });
      }
    } catch (error) {
      console.error('Failed to fetch marketing activities:', error);
      if (currentOffset === 0) setActivities([]);
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  // Restore dari cache saat mount, atau fetch fresh jika tidak ada cache
  useEffect(() => {
    const cached = listCache.restore('marketing-activities');
    if (cached) {
      setActivities(cached.items);
      setOffset(cached.cursor);
      setHasMore(cached.hasMore);
      setLoading(false);
      restoredRef.current = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const el = document.getElementById('scrollableDiv');
          if (el) el.scrollTop = cached.scrollTop;
        });
      });
      listCache.clear('marketing-activities');
      setTimeout(() => { restoredRef.current = false; }, 0);
      return;
    }
    setOffset(0);
    fetchActivities(0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (restoredRef.current) return;
    if (offset > 0) {
      fetchActivities(offset);
    }
  }, [offset]);

  const getTypeColor = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'meeting': return { bg: '#EFF6FF', text: '#3B82F6', border: '#BFDBFE' };
      case 'visit': return { bg: '#ECFDF5', text: '#10B981', border: '#A7F3D0' };
      case 'call': return { bg: '#FFF7ED', text: '#F59E0B', border: '#FED7AA' };
      case 'email': return { bg: '#F5F3FF', text: '#8B5CF6', border: '#DDD6FE' };
      default: return { bg: '#F3F4F6', text: '#6B7280', border: '#E5E7EB' };
    }
  };

  const renderHeader = () => (
    <components.Header title={t.marketingActivity?.title || "Marketing Activities"} 
      goBack={true} 
      plus={true}
      onPlus={() => navigate('/CreateMarketingActivity')}
    />
  );

  const renderContent = () => (
    <main
      id="scrollableDiv"
      className="container"
      style={{ marginTop: 60, paddingBottom: 100, height: 'calc(100vh - 60px)', overflowY: 'auto' }}
    >
      {loading ? (
        <text.T16 style={{textAlign: 'center', marginTop: 40, color: '#9CA3AF'}}>{t.common?.loading || 'Loading activities...'}</text.T16>
      ) : activities.length === 0 ? (
        <div style={{textAlign: 'center', padding: '60px 20px', opacity: 0.6}}>
          <text.H5 style={{color: theme.colors.mainDark, marginBottom: 8}}>{t.marketingActivity?.noActivities || 'No Activities'}</text.H5>
          <text.T14 style={{color: '#6B7280'}}>{t.marketingActivity?.noActivitiesDesc || "You haven't created any marketing activities yet."}</text.T14>
        </div>
      ) : (
        <InfiniteScroll
          dataLength={activities.length}
          next={() => setOffset(prev => prev + 10)}
          hasMore={hasMore}
          loader={
            <div style={{ textAlign: 'center', padding: '10px 0' }}>
              <text.T14 style={{ color: '#6B7280', textAlign: 'center' }}>{t.activity?.loadingMore || 'Loading more...'}</text.T14>
            </div>
          }
          endMessage={
            <div style={{ textAlign: 'center', padding: '10px 0', marginBottom: 20 }}>
              <text.T14 style={{ color: '#6B7280', textAlign: 'center' }}>{t.activity?.allLoaded || 'All activities loaded'}</text.T14>
            </div>
          }
          scrollableTarget="scrollableDiv"
          style={{ overflow: 'visible' }}
        >
          <div style={{display: 'flex', flexDirection: 'column', gap: 16}}>
          {activities.map((act) => {
            const colors = getTypeColor(act.activity_type);
            return (
              <div key={act.id} onClick={() => {
                const el = document.getElementById('scrollableDiv');
                listCache.save('marketing-activities', { items: activities, cursor: offset, hasMore, scrollTop: el?.scrollTop || 0 });
                navigate('/DetailMarketingActivity', { state: { activity: act } });
              }} style={{ backgroundColor: 'white', borderRadius: 16, padding: 16, border: '1px solid #E5E7EB', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <div style={{ padding: '2px 8px', borderRadius: 8, fontSize: 12, fontWeight: '600', backgroundColor: colors.bg, color: colors.text, border: `1px solid ${colors.border}`, display: 'inline-block' }}>{act.activity_type}</div>
                  <div style={{display: 'flex', alignItems: 'center', gap: 4, color: '#9CA3AF'}}><TimeIcon size={16} /><text.T14 style={{fontSize: 12}}>{formatDateTime(act.datetime)}</text.T14></div>
                </div>
                <div>
                  <text.H5 style={{color: theme.colors.mainDark, marginBottom: 6, fontSize: 16, lineHeight: 1.4, textTransform: 'capitalize'}}>{act.subject}</text.H5>
                  <div style={{display: 'flex', gap: 8, alignItems: 'center', color: '#4B5563', marginBottom: 4}}><BusinessIcon size={18} color="#9CA3AF" /><text.T14 style={{fontWeight: '500', textTransform: 'capitalize'}}>{act.company_name}</text.T14></div>
                  {act.user_name && (
                    <div style={{display: 'flex', gap: 8, alignItems: 'center', color: '#6B7280'}}><PersonIcon size={16} color="#9CA3AF" /><text.T14 style={{fontSize: 13, textTransform: 'capitalize'}}>{act.user_name}</text.T14></div>
                  )}
                </div>
                {act.address && (
                  <div style={{display: 'flex', gap: 8, alignItems: 'flex-start', color: '#6B7280', borderTop: '1px dashed #E5E7EB', paddingTop: 12}}>
                    <LocationIcon size={18} color="#9CA3AF" style={{marginTop: 2, flexShrink: 0}} />
                    <text.T14 style={{fontSize: 13, lineHeight: 1.4}} numberOfLines={2}>{act.address}</text.T14>
                  </div>
                )}
              </div>
            );
          })}
          </div>
        </InfiniteScroll>
      )}
    </main>
  );

  return (
    <div id="screen" style={{ backgroundColor: '#FFFFFF', minHeight: '100vh' }}>{renderHeader()}{renderContent()}</div>
  );
};